import React from 'react';
import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';
import first from 'lodash/first';
import replace from 'lodash/replace';
import trim from 'lodash/trim';

import themeable from 'zignal-landscape/lib/themeable';
import CircleKeyIcon from 'zignal-landscape/lib/Icon/CircleKeyIcon';
import MfeFiltersIcon from 'zignal-landscape/lib/Icon/MfeFiltersIcon';
import DuplicateIcon from 'zignal-landscape/lib/Icon/DuplicateIcon';

import { generateUrlFromPathWithParams } from 'helpers/urlGenerator';
import { convertApiTimeToMoment, convertApiTimeToDatePickerTime } from 'helpers/timeFormat';
import { convertStrToAdditionalFilters, convertStrToSelectedLanguages, addDefaultElements } from '../../helpers';
import MfeIconWrapper from '../MfeIconWrapper/MfeIconWrapper';
import ToolTip from 'zignal-landscape/lib/ToolTip';

class SearchRow extends React.Component {
  /**
   * ZII queries are restricted. This will strip out from the terms any illegal char.
   *
   * How it works:
   * In first instance, ZII only accepts a single search term, so if multiple are present it will take the first one.
   * Then, ticker query is not allowed, so that if that is present in the search term then it will be removed as well.
   * @param searchTerms Array of search terms
   * @returns {string} A single search term that ZII supports.
   */
  static parseSearchTermsForZII(searchTerms) {
    const searchTerm = first(searchTerms);
    const tickerRegex = /ticker:([a-zA-Z])+/;
    return trim(replace(searchTerm, tickerRegex, ''));
  }

  constructor(props) {
    super(props);

    this.state = {
      discoverLinkCopied: false
    };

    this.handleOnRowClick = this.handleOnRowClick.bind(this);
    this.handleOnClipboardLinkClick = this.handleOnClipboardLinkClick.bind(this);
    this.handleOnZIILinkClick = this.handleOnZIILinkClick.bind(this);
  }

  getUrl(path) {
    const { startDate: start, endDate: end, searchTerms, settings={} } = this.props;
    // ZII is not capable of multiple searches so we send just the first one.
    const q = path === 'influence-intelligence'
      ? SearchRow.parseSearchTermsForZII(searchTerms)
      : JSON.stringify(searchTerms);
    const additionalFilters = settings.additionalFilters && settings.additionalFilters[0] && settings.additionalFilters.join('|');
    const selectedLanguages = settings.selectedLanguages && settings.selectedLanguages[0] && settings.selectedLanguages.join('|');
    const enabledKeywords = settings.enabledKeywords && settings.enabledKeywords[0] && settings.enabledKeywords.join('|');

    const params = { start, end, q };
    if (selectedLanguages) {
      params.l = selectedLanguages;
    }
    if (additionalFilters) {
      params.f = additionalFilters;
    }
    if (enabledKeywords) {
      params.k = enabledKeywords;
    }
    return generateUrlFromPathWithParams(`/${path}/`, params);
  }

  handleOnRowClick() {
    const {
      populateSearchForm,
      fetchRecentSearchResults,
      toggleRecentSearches,
      startDate: start,
      endDate: end,
      timestamp,
      searchTerms,
      timezone,
      settings
    } = this.props;
    const startDate = convertApiTimeToDatePickerTime(start, timezone);
    const endDate = convertApiTimeToDatePickerTime(end, timezone);
    const additionalFilters = convertStrToAdditionalFilters(settings.additionalFilters);
    const translations = addDefaultElements([], []); // clear translations
    const selectedLanguages = convertStrToSelectedLanguages(settings.selectedLanguages);
    populateSearchForm({ searchTerms, startDate, endDate, timestamp, additionalFilters, selectedLanguages, translations });
    fetchRecentSearchResults(settings.enabledKeywords);
    toggleRecentSearches();
  }

  formatDateRange() {
    const { startDate, endDate, timezone } = this.props;
    const start = convertApiTimeToMoment(startDate, timezone).format('lll');
    const end = convertApiTimeToMoment(endDate, timezone).format('lll');
    return `${start} - ${end}`;
  }

  handleOnClipboardLinkClick(event) {
    event.stopPropagation();
    const url = this.getUrl('discover');
    copy(url);
    this.setState({discoverLinkCopied: true});
    setTimeout(() => {
      this.setState({discoverLinkCopied: false});
    }, 2000);
  }

  handleOnZIILinkClick(event) {
    event.stopPropagation();
    const url = this.getUrl('influence-intelligence');
    window.open(url, '_blank');
  }

  render() {
    const { styles, searchTerms, settings, colors } = this.props;
    const { additionalFilters=[], selectedLanguages=[] } = settings;
    // not doing anything with this now, may want to in the future
    // const { discoverLinkCopied } = this.state;
    const termLength = searchTerms.length - 1;
    return (
      <div className={styles.SearchRow}>
        <div className={styles.outerContainer}>
          <div className={styles.innerContainer} onClick={this.handleOnRowClick}>
            {searchTerms.map((term, i) =>
              (<div key={i} className={styles.searchRow}>
                <div className={styles.termContainer}>
                  <div className={styles.topSection}>
                    <div className={styles.circleIcon} style={{color: colors[i]}}><CircleKeyIcon height="15px" width="15px" /></div>
                    <div className={styles.term}>{term}</div>
                  </div>
                  <div className={styles.bottomSection}>
                    <div className={`${styles.languages} ${termLength === i ? styles.withDate : ''}`}>{(selectedLanguages[i] || '').split(',').join(', ')}</div>
                    { termLength === i && <div className={styles.dateRange}>{this.formatDateRange()}</div> }
                  </div>
                </div>
                <div className={styles.additionalFilter}>
                  { additionalFilters[i] && <MfeIconWrapper icon={MfeFiltersIcon} showDot notButton /> }
                </div>
              </div>)
            )}
          </div>
        </div>
        <div className={styles.copyContainer}>
          <ToolTip tooltipText="Copy Link">
            <DuplicateIcon className={styles.copy} onClick={this.handleOnClipboardLinkClick} />
          </ToolTip>
        </div>
      </div>
    );
  }
}

SearchRow.propTypes = {
  styles: PropTypes.object,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  searchTerms: PropTypes.array,
  settings: PropTypes.object,
  timestamp: PropTypes.string,
  timezone: PropTypes.string,
  colors: PropTypes.array,
  populateSearchForm: PropTypes.func,
  fetchRecentSearchResults: PropTypes.func,
  toggleRecentSearches: PropTypes.func,
  onTranslateKeywords: PropTypes.func
};

SearchRow.defaultProps = {
  colors: [],
  searchTerms: [],
  settings: {}
};

export default themeable(SearchRow);
